import { collection, deleteDoc, doc, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { deleteObject, listAll, ref as storageRef } from 'firebase/storage';
import { auth, db, storage } from 'shared/services/firebase';
import { getQueryOnce } from 'shared/services/firestore';
import { uploadFile } from 'shared/services/storage';
import { mapPurchaseOrder, PurchaseOrder, PurchaseOrderStatus } from 'domains/billing/model/purchaseOrder';

const PURCHASE_ORDERS = 'purchase_orders';

export async function uploadPurchaseOrderDocument(
  customerId: string,
  file: Blob | Uint8Array | ArrayBuffer,
  fileName: string,
): Promise<{ storagePath: string; downloadUrl: string; fileName: string }> {
  const safeName = fileName.replace(/[^A-Za-z0-9._-]/g, '_') || 'purchase-order.pdf';
  const storagePath = 'purchase_orders/' + customerId + '/' + Date.now() + '_' + safeName;
  const downloadUrl = await uploadFile(storagePath, file, {
    contentType: 'application/pdf',
    cacheControl: 'no-cache',
  });
  return { storagePath, downloadUrl, fileName };
}

export async function deletePurchaseOrderDocumentsForCustomer(customerId: string): Promise<void> {
  try {
    const result = await listAll(storageRef(storage, 'purchase_orders/' + customerId));
    await Promise.all(result.items.map((item) => deleteObject(item)));
  } catch (err) {
    console.error('[storage] deletePurchaseOrderDocumentsForCustomer(%s):', customerId, err);
  }
}

/** Port of aligned `getPurchaseOrdersForCustomer`: customer-scoped read, newest created first. */
export async function getPurchaseOrdersForCustomer(customerId: string): Promise<PurchaseOrder[]> {
  const rows = await getQueryOnce(
    query(collection(db, PURCHASE_ORDERS), where('customerId', '==', customerId)),
    mapPurchaseOrder,
  );
  return rows.sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
}

export interface NewPurchaseOrderInput {
  customerId: string;
  poNumber?: string | null;
  vendorName?: string | null;
  maxSpendMicroUsd?: number | null;
  currency?: string;
  startDate?: string | null;
  endDate?: string | null;
  paymentTerms?: string | null;
  notes?: string | null;
  status?: PurchaseOrderStatus;
  markdownBody?: string | null;
  documentStoragePath?: string | null;
  documentDownloadUrl?: string | null;
  documentFileName?: string | null;
  uploadedAt?: string | null;
  uploadedByFirebaseId?: string | null;
}

export async function createPurchaseOrder(input: NewPurchaseOrderInput): Promise<PurchaseOrder> {
  const ref = doc(collection(db, PURCHASE_ORDERS));
  const now = new Date().toISOString();
  const data = {
    id: ref.id,
    customerId: input.customerId,
    poNumber: input.poNumber ?? null,
    vendorName: input.vendorName ?? null,
    maxSpendMicroUsd: input.maxSpendMicroUsd ?? null,
    currency: input.currency?.trim() || 'USD',
    startDate: input.startDate ?? null,
    endDate: input.endDate ?? null,
    paymentTerms: input.paymentTerms ?? null,
    markdownBody: input.markdownBody ?? null,
    documentStoragePath: input.documentStoragePath ?? null,
    documentDownloadUrl: input.documentDownloadUrl ?? null,
    documentFileName: input.documentFileName ?? null,
    notes: input.notes ?? null,
    status: input.status ?? 'active',
    uploadedAt: input.uploadedAt ?? null,
    uploadedByFirebaseId: input.uploadedByFirebaseId ?? auth.currentUser?.uid ?? null,
    createdAt: now,
    updatedAt: now,
  };
  await setDoc(ref, data);
  return mapPurchaseOrder(ref.id, data);
}

export type PurchaseOrderUpdate = Partial<Omit<PurchaseOrder, 'id' | 'customerId' | 'createdAt'>>;

/** Full-field merge update, matching aligned's edit PO dialog. */
export async function updatePurchaseOrder(id: string, fields: PurchaseOrderUpdate): Promise<void> {
  await updateDoc(doc(db, PURCHASE_ORDERS, id), { ...fields, updatedAt: new Date().toISOString() });
}

/** Deletes only the Firestore PO record; the source PDF remains in Storage,
 * matching aligned's individual-delete behavior (customer cascade removes
 * the customer prefix separately). */
export function deletePurchaseOrder(id: string): Promise<void> {
  return deleteDoc(doc(db, PURCHASE_ORDERS, id));
}
